import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export type PrizeDto = {
  PrizeId: number;
  code: string;
  name: string;
  type: string;
  valueJson: string;
};

type Props = {
  count?: number; // số hộp
  onPick: () => Promise<PrizeDto>; // gọi redeem, trả prize
  onDone?: (p: PrizeDto) => void;
};

export default function GiftBoxGame({ count = 9, onPick, onDone }: Props) {
  const { t } = useTranslation();

  const [pickedIndex, setPickedIndex] = useState<number | null>(null);
  const [opening, setOpening] = useState(false);
  const [result, setResult] = useState<PrizeDto | null>(null);

  const grid = useMemo(() => {
    return Array.from({ length: count }, (_, i) => i);
  }, [count]);

  const handlePick = async (idx: number) => {
    if (opening || result) return;

    setPickedIndex(idx);
    setOpening(true);

    try {
      const prize = await onPick();

      window.setTimeout(() => {
        setResult(prize);
        setOpening(false);
        onDone?.(prize);
      }, 650);
    } catch (e) {
      setOpening(false);
      setPickedIndex(null);
      throw e;
    }
  };

  const reset = () => {
    setPickedIndex(null);
    setResult(null);
    setOpening(false);
  };

  return (
    <div className="text-center">
      {/* title */}
      <div className="mb-4">
        <p className="text-[9px] uppercase tracking-[0.6em] text-black/35">
          {t("voucherGame.giftBox.kicker")}
        </p>
        <h3 className="mt-3 text-[14px] uppercase tracking-[0.35em] text-black/80">
          {t("voucherGame.giftBox.title")}
        </h3>
        <div className="mt-4 h-px w-10 bg-black/15 mx-auto" />
      </div>

      {/* board */}
      <div className="mx-auto max-w-[420px] rounded-2xl border border-black/10 bg-white/40 px-4 sm:px-5 py-5">
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {grid.map((idx) => {
            const isPicked = pickedIndex === idx;
            const isDisabled = opening || !!result;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => handlePick(idx)}
                disabled={isDisabled}
                aria-label={t("voucherGame.giftBox.pickBoxAria", { n: idx + 1 })}
                className={[
                  "relative h-[82px] sm:h-[92px] rounded-2xl",
                  "border transition-all duration-200",
                  "flex items-center justify-center",
                  "select-none",
                  isDisabled ? "cursor-not-allowed opacity-70" : "hover:bg-white/60",
                  isPicked
                    ? "border-black/25 bg-white/70 shadow-sm"
                    : "border-black/15 bg-white/35",
                ].join(" ")}
              >
                {/* icon */}
                <span
                  className={[
                    "text-[28px] sm:text-[30px] transition-transform duration-200",
                    isPicked ? "scale-[1.03]" : "scale-100",
                  ].join(" ")}
                >
                  🎁
                </span>

                {/* loading overlay on picked */}
                {opening && isPicked && (
                  <span className="absolute inset-0 rounded-2xl bg-white/70 backdrop-blur flex items-center justify-center">
                    <span className="h-5 w-5 rounded-full border-2 border-black/20 border-t-black/60 animate-spin" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* hint */}
        {!result && (
          <p className="mt-5 text-[10px] uppercase tracking-[0.35em] text-black/35">
            {opening
              ? t("voucherGame.giftBox.opening")
              : t("voucherGame.giftBox.tapToOpen")}
          </p>
        )}

        {/* result */}
        {result && (
          <div className="mt-6">
            <p className="text-[9px] uppercase tracking-[0.6em] text-black/35">
              {t("voucherGame.giftBox.rewardKicker")}
            </p>

            <div className="mt-4 rounded-2xl border border-black/10 bg-white/60 px-4 py-4">
              <p className="text-[11px] text-black/60">
                {t("voucherGame.giftBox.youGot")}
              </p>

              <p className="mt-1 text-[12px] text-black">
                <span className="font-medium">{result.name}</span>{" "}
                <span className="text-black/50">
                  ({result.type}) —{" "}
                  <span className="font-medium text-black/70">
                    {result.code}
                  </span>
                </span>
              </p>
            </div>

            <button
              type="button"
              onClick={reset}
              className="mt-4 w-full h-10 rounded-full border border-black/20 bg-white/60
                         text-[9.5px] uppercase tracking-[0.55em] text-black/65
                         hover:bg-white/80 transition"
            >
              {t("voucherGame.giftBox.playAgain")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
